import React, { FC, useCallback, useContext, useMemo } from 'react';
import {
  createServerSidePaginatedFetcher,
  IServerSidePaginatedFetcherParams,
  IServerSorting,
} from '../contexts/Fetcher';
import { PageHeader } from '../components/PageHeader/PageHeader';
import { UrlQueryDataGrid } from '../components/UrlQueryDataGrid/UrlQueryDataGrid';
import { GridColumns, GridRowParams } from '@mui/x-data-grid';
import { getDateValueFormatter } from '../valueFormatters/DateValueFormatter';
import { DateFormat } from '../helpers/dateFormat';
import { getStringEqualsOperator } from '../filterOperators/stringEqualsOperator';
import { getStringDiffersOperator } from '../filterOperators/stringDiffersOperator';
import { getDateAfterFilterOperator, getDateBeforeFilterOperator } from '../filterOperators/dateFilterOperators';
import { EasyFilter } from '../components/EasyFilter/EasyFilter';
import { IQuickFiltersProps } from '../components/EasyFilter/QuickFilters';
import { CellContentTooltip } from '../components/CellContentTooltip/CellContentTooltip';
import { useTranslation } from 'react-i18next';
import { createStatusChipComponent } from '../components/StatusChip/StatusChip';
import { getStringContainsOperator } from '../filterOperators/stringContainsOperator';
import styled from '@mui/styles/styled';
import { apiUser } from '../resources/api/apiUser';
import { adminRoles, EAdminRole } from '../const/roles';
import { useQueryParam } from '../hooks/useQueryParam';
import { AuthenticatedRoutes } from '../routers/AuthenticatedRoutes';
import { useIsGuardedRouteAllowed } from '../resources/GuardedRouteLink';
import { FilteringContextProvider } from '../contexts/FilteringContext';

const { useGetAdmins } = apiUser;

export const AdminsFetcher = createServerSidePaginatedFetcher(() => {
  const { execute } = useGetAdmins();
  const roleIds = useQueryParam('role_ids');

  return useCallback(
    (pagination: IServerSidePaginatedFetcherParams) => {
      return execute(pagination, roleIds?.split(',') ?? void 0);
    },
    [execute, roleIds]
  );
});

const StatusRenderer = createStatusChipComponent({
  verified: ['common.accountStatus.verified', 'lime'],
  unverified: ['common.accountStatus.unverified', 'orange'],
  mfaEnabled: ['common.accountStatus.mfaEnabled', 'lime'],
});

const RoleChip = createStatusChipComponent({
  [EAdminRole.Admin]: ['roles.all.admin', 'default'],
  [EAdminRole.Sales]: ['roles.all.sales', 'blue'],
});

const RoleChipWrapper = styled('span')(({ theme }) => ({
  marginRight: theme.spacing(0.5),
}));

/** @todo check if filtering&sorting work well with identities endpoints and adjust that if needed */
const AdminsContent: FC = () => {
  const { data } = useContext(AdminsFetcher.Context);

  const { t } = useTranslation();

  const columns = useMemo<GridColumns>(() => {
    return [
      {
        field: 'first_name',
        headerName: t('common.tableHeader.firstName'),
        flex: 1,
        filterOperators: [getStringContainsOperator(), getStringEqualsOperator(), getStringDiffersOperator()],
        renderCell: (params) => <CellContentTooltip>{params.value}</CellContentTooltip>,
      },
      {
        field: 'last_name',
        headerName: t('common.tableHeader.lastName'),
        flex: 1,
        filterOperators: [getStringContainsOperator(), getStringEqualsOperator(), getStringDiffersOperator()],
        renderCell: (params) => <CellContentTooltip>{params.value}</CellContentTooltip>,
      },
      {
        field: 'email',
        headerName: t('common.tableHeader.email'),
        flex: 2,
        filterOperators: [getStringContainsOperator(), getStringEqualsOperator()],
        renderCell: (params) => (
          <CellContentTooltip>
            {params.row.role === 'sales' ? (
              <RoleChipWrapper>
                <RoleChip {...params} value={true} />
              </RoleChipWrapper>
            ) : null}
            {params.value}
          </CellContentTooltip>
        ),
      },
      {
        field: 'user_roles',
        headerName: t('common.tableHeader.role'),
        width: 150,
        filterable: false,
        sortable: false,
        renderCell: (params: { value: string[] }) => (
          <div>
            {params.value?.map((role) => {
              const found = adminRoles.find((adminRole) => adminRole.id === role);
              if (found) {
                return <RoleChip key={found.id} value={found.role} />;
              }
            })}
          </div>
        ),
      },
      {
        field: 'phone_number',
        headerName: t('common.tableHeader.phone'),
        width: 120,
        filterable: false,
        sortable: false,
        renderCell: (params) => <CellContentTooltip>{params.value}</CellContentTooltip>,
      },
      {
        field: 'status',
        headerName: t('common.tableHeader.status'),
        width: 120,
        renderCell: (params) => {
          return (
            <StatusRenderer
              {...params}
              value={!params.row.verified ? 'unverified' : params.row.MFAEnabled ? 'mfaEnabled' : 'verified'}
            />
          );
        },
        filterable: false,
        sortable: false,
      },
      {
        field: 'updated_at',
        headerName: t('common.tableHeader.updated'),
        valueFormatter: getDateValueFormatter(DateFormat.DateMedium),
        width: 130,
        filterOperators: [getDateBeforeFilterOperator(), getDateAfterFilterOperator()],
      },
      {
        field: 'created_at',
        headerName: t('common.tableHeader.created'),
        valueFormatter: getDateValueFormatter(DateFormat.DateMedium),
        width: 130,
        filterOperators: [getDateBeforeFilterOperator(), getDateAfterFilterOperator()],
      },
      {
        field: 'verified',
        headerName: t('common.tableHeader.updated'),
        hide: true,
        hideable: false,
        sortable: false,
      },
      {
        field: 'role',
        headerName: t('admin.role'),
        hide: true,
        hideable: false,
        sortable: false,
      },
    ];
  }, []);

  const rows = data ? data.payload.data : null;

  const getRowLink = useCallback((params: GridRowParams) => {
    return AuthenticatedRoutes.User(params.row.id);
  }, []);
  const isGetRowLinkAllowed = useIsGuardedRouteAllowed('User', ['any']);

  if (!rows || !data) return null;
  return (
    <UrlQueryDataGrid
      columns={columns}
      rows={rows}
      disableExtendRowFullWidth={false}
      autoHeight={true}
      disableSelectionOnClick={true}
      paginationMode={'server'}
      filterMode={'server'}
      sortingMode={'server'}
      rowCount={data.payload.total_items}
      getRowLink={isGetRowLinkAllowed ? getRowLink : void 0}
    />
  );
};

const defaultSorting: IServerSorting = {
  sortBy: 'created_at',
  sortOrder: 'desc',
};

export const AdminsPage: FC = () => {
  const { t } = useTranslation();

  const quickFilters: IQuickFiltersProps['quickFilters'] = useMemo(
    () =>
      new Map([
        [
          t('admins.quickFilters.allRoles'),
          {
            extra: {
              role_ids: adminRoles.map(({ id }) => id).join(','),
            },
            isDefault: true,
          },
        ],
        [
          t('admins.quickFilters.admins'),
          {
            extra: {
              role_ids: adminRoles.find(({ role }) => role === EAdminRole.Admin)?.id,
            },
          },
        ],
        [
          t('admins.quickFilters.sales'),
          {
            extra: {
              role_ids: adminRoles.find(({ role }) => role === EAdminRole.Sales)?.id,
            },
          },
        ],
      ]),
    []
  );

  return (
    <>
      <PageHeader title={t('admins.header')} />

      <FilteringContextProvider quickFilters={quickFilters} defaultSorting={defaultSorting}>
        <EasyFilter quickFilters={quickFilters} />

        <AdminsFetcher.WAF>
          <AdminsContent />
        </AdminsFetcher.WAF>
      </FilteringContextProvider>
    </>
  );
};
